import { Button, Typography } from 'components';
import { useTranslations } from 'next-intl';
import { FC, useCallback } from 'react';
import { connect } from 'react-redux';

import { CloseCircleIcon, FileErrorIcon, RetryIcon } from 'lib/svgs/Icons';
import { cn } from 'utils/cn';
import { getExtension } from 'utils/getExtension';
import {
  removeFileToUpload,
  retryFileToUpload,
  useSelectionContentFlow,
  useUploadingContentFlow
} from './_actions';

interface IUploadErrorFlow {
  uploadingError: string;
  filesToUpload: FileList;
  allowedTypes: string[];
  useSelectionContentFlow: () => void;
  useUploadingContentFlow: () => void;
  removeFileToUpload: (fileName: string) => void;
  retryFileToUpload: (file: File[]) => void;
}

const UploadErrorFlow: FC<IUploadErrorFlow> = ({
  filesToUpload,
  allowedTypes,
  removeFileToUpload,
  retryFileToUpload,
  useSelectionContentFlow: selectionContentFlow,
  useUploadingContentFlow: uploadingContentFlow
}) => {
  const t = useTranslations();

  const filesArray = Array.from(filesToUpload);

  const isSomeAllowed = filesArray.some((file) => {
    const extension = getExtension(file);
    return allowedTypes?.includes(extension);
  });

  const handleRemoveFile = useCallback(
    (fileName: string) => () => {
      removeFileToUpload(fileName);

      if (filesArray?.length === 1) selectionContentFlow();
    },
    [filesArray]
  );

  const handleRetry = (file: File) => () => {
    retryFileToUpload([file]);
    uploadingContentFlow();
  };

  const handleRetryAll = useCallback(() => {
    const filesFiltered = filesArray.filter((file) => {
      const extension = getExtension(file);
      return allowedTypes?.includes(extension);
    });
    retryFileToUpload(filesFiltered);
    uploadingContentFlow();
  }, [filesArray]);

  return (
    <div className="ordering_form__content_frame rounded shadow-custom">
      <div>
        <div className="mb-6">
          <Typography variant="bold5xl" className="mb-8 px-8 text-primary-red-100">
            {t('ordering_form.error.header')}
          </Typography>
        </div>
        <div className="flex flex-1 flex-col gap-2">
          {filesArray.map((file, index) => {
            const extension = getExtension(file);
            const isAllowed = allowedTypes?.includes(extension);

            return (
              <div
                key={file?.name + index}
                className={cn(
                  'relative flex rounded-[8px] border border-tertiary-600 p-4',
                  isAllowed ? 'pb-2' : ''
                )}
              >
                <div className="absolute right-2 top-2 z-[1000]">
                  <Button
                    size="small"
                    variant="icon"
                    color="neutral"
                    className="!w-8 rounded-full"
                    onClick={handleRemoveFile(file.name)}
                    startAdornment={<CloseCircleIcon className="scale-75" />}
                  />
                </div>
                <div className="pr-4">{<FileErrorIcon className="pl-[2px]" />}</div>
                <div className="flex flex-1 flex-col items-start">
                  {
                    <Typography variant="light4" className="pb-[2px] !leading-4 text-secondary-red">
                      {isAllowed
                        ? t('translation_memory.modal_upload.card_file.error_upload.title')
                        : t('translation_memory.modal_upload.card_file.error_invalid_type.title')}
                    </Typography>
                  }
                  <Typography variant="medium4" className="pb-[2px] !leading-5 text-tertiary-100">
                    {file.name}
                  </Typography>
                  <Typography variant="light5" className="pt-2 text-left text-tertiary-200">
                    {isAllowed
                      ? t('translation_memory.modal_upload.card_file.error_upload.paragraph')
                      : t('translation_memory.modal_upload.card_file.error_invalid_type.paragraph')}
                  </Typography>
                  {isAllowed && (
                    <Button
                      variant="icon"
                      size="small"
                      colorIcon="blue"
                      startAdornment={<RetryIcon />}
                      classNameTypography="text-secondary-link"
                      label={t('translation_memory.modal_upload.card_file.retry')}
                      onClick={handleRetry(file)}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-8 flex justify-center gap-4 ">
          <Button size="small" variant="text" onClick={selectionContentFlow}>
            {t('ordering_form.error.back')}
          </Button>
          {isSomeAllowed && (
            <Button
              size="small"
              className="!px-3"
              colorIcon="white"
              color="primary"
              onClick={handleRetryAll}
              endAdornment={<RetryIcon />}
            >
              {t('ordering_form.error.retry_all')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default connect(
  ({ uploadingError, filesToUpload, staticData: { allowedTypes } }) => ({
    uploadingError,
    filesToUpload,
    allowedTypes
  }),
  {
    removeFileToUpload,
    retryFileToUpload,
    useSelectionContentFlow,
    useUploadingContentFlow
  }
)(UploadErrorFlow);
